import { Layout, Table, Input, Modal } from "antd";
import { Content } from "antd/es/layout/layout";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import Swal from "sweetalert2";

import { ReactComponent as ICON_ARROW_BACK_MENU } from "@assets/svgs/ICON_ARROW_BACK_MENU.svg";
import { Button } from "@components/Button";
import Header from "@components/Header";
import { Icon } from "@components/Icon";
import { InnerContents } from "@components/InnerContents";
import { SideMenu } from "@components/SideMenu";
import { Text } from "@components/Text";
import { useSocketContext } from "@contexts/socket/socketContext";
import { setLastedDeviceForceSync } from "@redux/reducers/lastedDeviceForceSync.reducers";
import { COLOR_GREEN, COLOR_GREY, COLOR_WHITE } from "@static/COLORS";
import { CONTENT_STYLES } from "@static/CONTENT_STYLES";
import { KEY_DEVICE_MANAGEMENT_DEVICES_ADD } from "@static/PERMISSION_KEY";
import { FILTER_SIZE, MENU_SIZE, MODAL_SIZE, PAGE_SIZE } from "@static/SIZE";
import { getDeviceListTableColumns } from "@static/TABLE/BASE_DEVICE_LIST_TABLE_COLUMNS";
import { getAuthUserPermissions } from "@utils/getAuthUserPermissions";
import { getAuthUserProfile } from "@utils/getAuthUserProfile";
import { fetchAPI } from "@utils/https/fetchAPI";
import { useShowConfirmation } from "@utils/useShowConfirmation";

import { FormCreate } from "./FormCreate";
import { FormCurrentCampaign } from "./FormCurrentCampaign";
import { FormEdit } from "./FormEdit";

const SectionCreate = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const SectionMainAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${COLOR_WHITE};
  padding: 8px;
  border-radius: 6px;
  height: 50px;
  box-sizing: border-box;
`;

const SectionLocationTextContainer = styled.div`
  margin-right: 8px;
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 4px;
`;

const Line = styled.div`
  height: 20px;
  width: 1px;
  background: ${COLOR_GREY};
`;

export default () => {
  const dispatch = useDispatch();

  const socket = useSocketContext();
  const showConfirmation = useShowConfirmation();
  const navigate = useNavigate();
  const location = useLocation();
  const permission = getAuthUserPermissions();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filterText, setFilterText] = useState("");
  const [dataList, setDataList] = useState([]);

  const [floorList, setFloorList] = useState([]);

  const [campaignList, setCampaignList] = useState([]);

  const [selectedData, setSelectedData] = useState(null);

  const customerId = _.get(location, ["state", "customerId"]);
  const locationId = _.get(location, ["state", "locationId"]);
  const locationName = _.get(location, ["state", "locationName"]);

  const lastedDeviceForceSync = useSelector(({ lastedDeviceForceSync }) => {
    return _.get(lastedDeviceForceSync, ["data"]);
  });

  const [isModalVisible, setIsModalVisible] = useState({
    isOpen: false,
    modalType: null,
  });
  const authUserProfile = getAuthUserProfile();
  // const user_type = _.get(authUserProfile, ["user_type"]);

  const onHandleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const onHandleFilterChange = (e) => {
    const value = _.get(e, ["target", "value"]);
    setFilterText(value);
  };

  const onHandleCreate = ({ modalType }) => {
    setIsModalVisible({ isOpen: true, modalType });
  };

  const onHandleEdit = ({ item }) => {
    setSelectedData(item);
    setIsModalVisible({ isOpen: true, modalType: "edit" });
  };

  const onHandleDelete = async ({ item }) => {
    const id = _.get(item, ["id"]);
    const resp = await fetchAPI({
      method: "delete",
      path: `/api/v1/device-delete/${id}`,
      isAuth: true,
    });
    if (_.get(resp, ["status"])) {
      toast.success("Delete Device Success.");
      const updateDataList = _.chain(dataList)
        .filter((item) => {
          return _.get(item, ["id"]) !== id;
        })
        .map((item, index) => {
          return { ...item, index: index + 1 };
        })
        .value();
      setDataList(updateDataList);
    }
  };

  const filteredDataSource = _.filter(dataList, (item) => {
    const itemName = _.chain(item).get(["name"]).toLower().value();
    const lowerFilterText = _.toLower(filterText);
    return _.includes(itemName, lowerFilterText);
  });

  const initData = async () => {
    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/devices",
      isAuth: true,
      data: { location_id: locationId },
    });
    console.log("resp :>> ", resp);

    if (_.get(resp, ["status"])) {
      const devices = _.chain(resp)
        .get(["result", "devices"])
        .map((item, index) => {
          const deviceGroupDetails = _.get(item, ["deviceGroupDetails"]);
          const device_group_id =
            _.size(deviceGroupDetails) > 0
              ? _.chain(deviceGroupDetails)
                  .first()
                  .get(["device_group_id"])
                  .value()
              : null;

          return { key: index, index: index + 1, ...item, device_group_id };
        })
        .value();
      const floors = _.chain(resp)
        .get(["result", "floors"])
        .map((item) => {
          return { label: _.get(item, ["name"]), value: _.get(item, ["id"]) };
        })
        .value();

      const campaigns = _.chain(resp)
        .get(["result", "campaigns"])
        .map((item) => {
          return { label: _.get(item, ["name"]), value: _.get(item, ["id"]) };
        })
        .value();

      setCampaignList(campaigns);
      setFloorList(floors);
      setDataList(devices);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const onHandleSocketMessageUpdateStatus = ({ message }) => {
    console.log("onHandleSocketMessageUpdateStatus message :>> ", message);
    const messagePlayerId = _.get(message, ["player_id"]);
    const messageDeviceDownloadProgress = _.get(message, ["download_progress"]);

    const downloadProgressJson = _.isString(messageDeviceDownloadProgress)
      ? JSON.parse(messageDeviceDownloadProgress)
      : messageDeviceDownloadProgress;
    const updateDataList = _.map(dataList, (item) => {
      return _.get(item, ["player_id"]) === messagePlayerId
        ? {
            ...item,
            ...message,
            download_progress: downloadProgressJson,
          }
        : item;
    });
    setDataList(updateDataList);
  };

  const onHandleSocketUpdateDeviceOnline = ({ message }) => {
    console.log("🟢 onHandleDeviceOnline message :>> ", message);
    console.log("dataList :>> ", dataList);
    console.log("============================= :>> ");

    setDataList((prev) => {
      const updatedList = _.map(prev, (item) => {
        const playerId = _.get(item, "player_id");
        if (_.has(message, playerId)) {
          return {
            ...item,
            isOnline: true,
            lastSeen: _.get(message, [playerId]),
          };
        }

        return item;
      });

      return updatedList;
    });
  };

  const onHandleSocketUpdateDeviceOffline = ({ message }) => {
    console.log("🔴 onHandleDeviceOffline message :>> ");
    setDataList((prev) => {
      const updatedList = _.map(prev, (item) => {
        const playerId = _.get(item, "player_id");
        if (_.has(message, playerId)) {
          return {
            ...item,
            isOnline: false,
            lastSeen: _.get(message, [playerId]),
          };
        }

        return item;
      });

      return updatedList;
    });
  };

  // const onHandleSocketMessageUpdateDownloadProgress = ({ message }) => {
  //   const messageDeviceId = _.get(message, ["id"]);
  //   const messageDeviceDownloadProgress = _.get(message, ["download_progress"]);
  //   console.log(
  //     "messageDeviceDownloadProgress :>> ",
  //     messageDeviceDownloadProgress
  //   );
  //   if (messageDeviceDownloadProgress) {
  //     // const downloadProgressJson = JSON.parse(messageDeviceDownloadProgress);
  //     // const progressPercentage = _.chain(downloadProgressJson)
  //     //   .sumBy("success")
  //     //   .toInteger()
  //     //   .thru((sum) => {
  //     //     return sum > 0 ? (sum / _.size(downloadProgressJson)) * 100 : 0;
  //     //   })
  //     //   .value();
  //     // const messagePlayerUpdated_at = _.get(message, ["updated_at"]);
  //     const updateDataList = _.map(dataList, (item) => {
  //       console.log("updateDataList item :>> ", item);
  //       return _.get(item, ["id"]) === messageDeviceId
  //         ? {
  //             ...item,
  //             download_progress: messageDeviceDownloadProgress,
  //           }
  //         : item;
  //     });
  //     setDataList(updateDataList);
  //   }
  // };

  const onHandleReceivedStartWhen = ({ message }) => {
    console.log("message :>> ", message);
  };
  useEffect(() => {
    if (!socket) {
      return undefined;
    }
    socket.on(
      "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-STATUS",
      onHandleSocketMessageUpdateStatus
    );

    socket.on(
      "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-ONLINE",
      onHandleSocketUpdateDeviceOnline
    );

    socket.on(
      "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-OFFLINE",
      onHandleSocketUpdateDeviceOffline
    );

    socket.on(
      "SERVICE-SEND-START-WHEN-TO-BACK-OFFICE",
      onHandleReceivedStartWhen
    );

    // socket.on(
    //   "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-DOWNLOAD-PROGRESS",
    //   onHandleSocketMessageUpdateDownloadProgress
    // );

    return () => {
      socket.off(
        "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-STATUS",
        onHandleSocketMessageUpdateStatus
      );

      socket.off(
        "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-ONLINE",
        onHandleSocketUpdateDeviceOnline
      );

      socket.off(
        "SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-OFFLINE",
        onHandleSocketUpdateDeviceOffline
      );

      socket.off(
        "SERVICE-SEND-START-WHEN-TO-BACK-OFFICE",
        onHandleReceivedStartWhen
      );

      // socket.off("SERVICE-EMIT-TO-BACK-OFFICE-UPDATE-DEVICE-DOWNLOAD-PROGRESS");
    };
  }, [socket, dataList]);

  const onHandleShutdownDevice = ({ item }) => {
    // console.log("item :>> ", item);
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-SEND-CONTROL-SHUT-DOWN-WITH-PLAYER-ID", {
      playerId,
    });
  };

  const onHandleRestartDevice = ({ item }) => {
    // console.log("item :>> ", item);
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-SEND-CONTROL-RESTART-WITH-PLAYER-ID", {
      playerId,
    });
  };

  const onHandleCloseElectronApp = ({ item }) => {
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-SEND-CONTROL-CLOSE-APP-WITH-PLAYER-ID", {
      playerId,
    });
  };

  const onHandleSoftSycGroup = ({ item }) => {
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-GROUP-SEND-SOFT-SYNCHRONIZED-TO-SERVICE", {
      playerList: [playerId],
    });
    toast.success("Soft Sync Success.");
  };

  const onHandleForceSycGroup = async ({ item }) => {
    const playerId = _.get(item, ["player_id"]);
    const now = dayjs();
    const previousPressTime = _.get(lastedDeviceForceSync, [playerId]);

    // if (now.isBefore(dayjs.unix(previousPressTime))) {
    //   console.log("ติด delay :>> ");
    //   Swal.fire({
    //     position: "center",
    //     icon: "success",
    //     title: `จะกดได้อีกใน ${dayjs
    //       .unix(previousPressTime)
    //       .format("hh:mm:ss")}`,
    //     showConfirmButton: false,
    //     timer: 1500,
    //   });
    //   return;
    // } else {
    const updatePressTime = dayjs().add(1, "minute").unix();
    const campaignPress = { [playerId]: updatePressTime };
    const updatedForceSync = {
      ...lastedDeviceForceSync,
      ...campaignPress,
    };
    dispatch(setLastedDeviceForceSync(updatedForceSync));

    const updateDataList = _.map(dataList, (dataItem) => {
      const isEqualPlayerId = _.get(dataItem, ["player_id"]) === playerId;
      return isEqualPlayerId
        ? { ...dataItem, download_progress: { status: "downloading" } }
        : dataItem;
    });

    setDataList(updateDataList);

    console.log("playerId :>> ", playerId);

    if (!_.isNil(playerId)) {
      const result = await fetchAPI({
        method: "post",
        path: "/api/v1/back-office-update-download-status",
        isAuth: true,
        data: { player_ids: [playerId] },
      });

      if (result?.status) {
        socket.emit("DEVICE-GROUP-SEND-FORCE-SYNCHRONIZED-TO-SERVICE", {
          playerList: [playerId],
        });
        toast.success("Force Sync Success.");
      }
    } else {
      toast.error("Player id not founds");
    }
    // }
  };

  // const onHandleForceSycGroup = ({ item }) => {
  //   const playerId = _.get(item, ["player_id"]);

  //   socket.emit("DEVICE-GROUP-SEND-FORCE-SYNCHRONIZED-TO-SERVICE", {
  //     playerList: [playerId],
  //   });
  //   toast.success("Force Sync Success.");
  // };

  const onHandleGotoNestedCampaign = ({ item }) => {
    console.log("item :>> ", item);
    const deviceGroupDetails = _.get(item, ["deviceGroupDetails"]);
    const campaignList = _.map(deviceGroupDetails, (deviceGroupDetailItem) => {
      const advertisingCampaign = _.get(deviceGroupDetailItem, [
        "advertisingCampaign",
      ]);
      const adsBlocks = _.chain(deviceGroupDetailItem)
        .get(["advertisingCampaign", "adsBlocks"])
        .value();

      const widgets = _.chain(deviceGroupDetailItem)
        .get(["advertisingCampaign", "widgets"])
        .value();
      return { ...advertisingCampaign, adsBlocks, widgets };
    });

    // // console.log("allCampaign :>> ", allCampaign);
    console.log("campaignList :>> ", campaignList);
    setSelectedData(campaignList);
    setIsModalVisible({ isOpen: true, modalType: "Current Campaign" });
  };

  const onHandleRotateScreen = ({ playerId, degree }) => {
    socket.emit("DEVICE-SEND-ROTATE-WINDOW-SCREEN-WITH-PLAYER-ID", {
      playerId: playerId,
      degree,
    });
    toast.success("Rotate Screen Success.");
  };

  const onHandleResetApp = ({ item }) => {
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-SEND-CONTROL-RESET-APP-WITH-PLAYER-ID", {
      playerId: playerId,
    });
    toast.success("Reset Screen...");
  };

  const onHandleReloadApp = ({ item }) => {
    const playerId = _.get(item, ["player_id"]);
    socket.emit("DEVICE-SEND-CONTROL-RELOAD-APP-WITH-PLAYER-ID", {
      playerId: playerId,
    });
    toast.success("Reload Screen Success.");
  };

  const handleRequestStartWhen = ({ item }) => {
    console.log("item :>> ", item);
    const playerId = _.get(item, ["player_id"]);
    navigate(`/prediction/${playerId}`);
    // socket.emit("BACK-OFFICE-REQUEST-ELECTRON-START-WHEN", {
    //   playerList: [playerId],
    // });
    // SERVICE-SEND-START-WHEN-TO-BACK-OFFICE
  };

  return (
    <Layout hasSider>
      <SideMenu />
      <Layout style={{ marginLeft: MENU_SIZE }}>
        <Header />
        <Content style={CONTENT_STYLES}>
          <InnerContents>
            <SectionMainAction>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <Button onClick={() => navigate(-1)} padding="0px">
                  <Icon
                    isCursor
                    width={14}
                    height={14}
                    style={{ marginRight: 8 }}
                  >
                    <ICON_ARROW_BACK_MENU />
                  </Icon>
                  <Text fontSize="18px" fontWeight="600">
                    Back
                  </Text>
                </Button>
                <Line />

                <Input
                  placeholder="Filter by name"
                  value={filterText}
                  onChange={(e) => onHandleFilterChange(e)}
                  style={{ width: FILTER_SIZE }}
                />
              </div>

              <SectionCreate>
                <SectionLocationTextContainer>
                  <Text
                    fontSize="16px"
                    fontWeight="400"
                    color="rgb(150 150 150)"
                  >
                    Project
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="400"
                    color="rgb(150 150 150)"
                  >
                    :
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="500"
                    isEllipsis
                    lines={1}
                    isBreak
                  >
                    {locationName}
                  </Text>
                </SectionLocationTextContainer>

                {_.includes(permission, KEY_DEVICE_MANAGEMENT_DEVICES_ADD) && (
                  <Button
                    onClick={() => onHandleCreate({ modalType: "create" })}
                    color={COLOR_GREEN}
                    fontColor={COLOR_WHITE}
                    padding="7px 15px"
                  >
                    Add Device
                  </Button>
                )}
              </SectionCreate>
            </SectionMainAction>

            <Table
              dataSource={filteredDataSource}
              columns={getDeviceListTableColumns({
                permission,
                onHandleGotoNestedCampaign,
                onHandleEdit,
                onHandleDelete,
                showConfirmation,
                onHandleShutdownDevice,
                onHandleRestartDevice,
                onHandleCloseElectronApp,
                onHandleSoftSycGroup,
                onHandleForceSycGroup,
                onHandleRotateScreen,
                onHandleReloadApp,
                onHandleResetApp,
                handleRequestStartWhen,
              })}
              pagination={{
                position: ["bottomRight"],
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
              }}
              // scroll={{ x: 1200 }}
              onChange={onHandleTableChange}
            />
          </InnerContents>
        </Content>
      </Layout>
      <Modal
        width={MODAL_SIZE}
        destroyOnClose={true}
        title={`${_.chain(isModalVisible)
          .get(["modalType"])
          .capitalize()
          .value()} device`}
        open={_.get(isModalVisible, ["isOpen"])}
        footer={null}
        onCancel={() =>
          setIsModalVisible((prev) => ({ ...prev, isOpen: false }))
        }
      >
        {_.get(isModalVisible, ["modalType"]) === "create" && (
          <FormCreate
            campaignList={campaignList}
            customerId={customerId}
            locationId={locationId}
            userProfile={authUserProfile}
            floors={floorList}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            dataList={dataList}
          />
        )}
        {_.get(isModalVisible, ["modalType"]) === "edit" && (
          <FormEdit
            floors={floorList}
            userProfile={authUserProfile}
            selectedData={selectedData}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setSelectedData={setSelectedData}
          />
        )}
        {_.get(isModalVisible, ["modalType"]) === "Current Campaign" && (
          <FormCurrentCampaign
            campaignList={selectedData}
            customerId={customerId}
            locationId={locationId}
          />
        )}
      </Modal>
    </Layout>
  );
};
